import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Newspaper, ArrowRight, Loader2, CalendarDays } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const mediaSrc = (u) => (u && u.startsWith("http") ? u : `${BACKEND_URL}${u}`);
const CATEGORIES = ["Tous", "Conseils", "Marché", "Actualités"];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
};

const CAT_COLOR = {
  Conseils: "bg-blue-50 text-[#2563EB]",
  "Marché": "bg-orange-50 text-[#C85A40]",
  "Actualités": "bg-emerald-50 text-emerald-600",
};

export default function Blog() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("Tous");

  useEffect(() => {
    setLoading(true);
    const q = cat === "Tous" ? "" : `?category=${encodeURIComponent(cat)}`;
    api.get(`/articles${q}`).then((r) => setArticles(r.data)).catch(() => setArticles([])).finally(() => setLoading(false));
  }, [cat]);

  return (
    <div className="App min-h-screen bg-slate-50" data-testid="blog-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="max-w-2xl mb-10">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] mb-3">
            <Newspaper className="w-4 h-4" /> Le blog
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-extrabold text-slate-900">
            Actualités &amp; Conseils Immo
          </h1>
          <p className="mt-4 text-lg text-slate-600">
            Nos analyses du marché, conseils pratiques et actualités pour vendre, acheter ou estimer
            votre bien en Auvergne-Rhône-Alpes.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-categories">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`blog-cat-${c}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                cat === c ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
          </div>
        ) : articles.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-3xl p-16 text-center" data-testid="blog-empty">
            <Newspaper className="w-10 h-10 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun article pour le moment. Revenez bientôt !</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {articles.map((a, i) => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Link
                  to={`/actualites/${a.slug}`}
                  data-testid={`blog-card-${a.slug}`}
                  className="group block bg-white border border-slate-200 rounded-3xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all h-full"
                >
                  {a.cover_image ? (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={mediaSrc(a.cover_image)}
                        alt={a.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                      <Newspaper className="w-10 h-10 text-slate-300" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${CAT_COLOR[a.category] || "bg-slate-100 text-slate-600"}`}>
                        {a.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <CalendarDays className="w-3 h-3" /> {fmtDate(a.created_at)}
                      </span>
                    </div>
                    <h2 className="font-display font-bold text-lg text-slate-900 leading-snug group-hover:text-[#2563EB] transition-colors">
                      {a.title}
                    </h2>
                    {a.excerpt && <p className="mt-2 text-sm text-slate-500 line-clamp-3">{a.excerpt}</p>}
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2563EB]">
                      Lire l'article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
